import { Component, computed, DestroyRef, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Page, RealEstateContact, RealEstateService, ViewQuery } from '@eadsysteme/ewsrealestateclient-v2';
import { Billing, BillingCustomCService, RealEstates } from '@eadsysteme/billingclient-v1';
import { SessionService } from '../../services/session.service';

export interface SearchResultItem {
	id: string;
	iconUrl?: string;
	iconTooltip?: string;
	primary: string;
	secondary?: string;
	tertiary?: string;
	meta?: string;
	onClick: () => void;
}

export interface SearchCategory {
	key: string;
	label: string;
	items: WritableSignal<SearchResultItem[]>;
	totalCount: WritableSignal<number>;
	showAll: WritableSignal<boolean>;
	loading: WritableSignal<boolean>;
}

const INITIAL_DISPLAY_COUNT = 3;
const MAX_PAGE_SIZE = 1000;

@Component({
	selector: 'ara-realestate-search-dialog',
	imports: [MatDialogModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, MatTooltipModule, TranslocoModule],
	templateUrl: './ara-realestate-search-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./ara-realestate-search-dialog.component.scss'],
})
export class AraRealestateSearchDialogComponent {
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialogRef = inject(MatDialogRef<AraRealestateSearchDialogComponent>);
	private readonly router = inject(Router);
	private readonly realEstateService = inject(RealEstateService);
	private readonly billingCustomCService = inject(BillingCustomCService);
	private readonly sessionService = inject(SessionService);

	readonly searchInput$ = new Subject<string>();
	readonly hasSearched = signal(false);

	readonly realEstateCategory: SearchCategory = {
		key: 'realEstates',
		label: 'Liegenschaften',
		items: signal([]),
		totalCount: signal(0),
		showAll: signal(false),
		loading: signal(false),
	};

	readonly billingCategory: SearchCategory = {
		key: 'billings',
		label: 'Abrechnungen in Liegenschaft',
		items: signal([]),
		totalCount: signal(0),
		showAll: signal(false),
		loading: signal(false),
	};

	readonly categories: SearchCategory[] = [this.realEstateCategory, this.billingCategory];

	readonly allCategoriesLoaded = computed(() => this.categories.every((category) => !category.loading()));
	readonly hasAnyResults = computed(() => this.categories.some((category) => category.items().length > 0));

	private cachedRealEstateContacts: RealEstateContact[] = [];

	constructor() {
		this.searchInput$.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((searchTerm: string) => {
			if (!searchTerm || searchTerm.trim().length < 2) {
				this.resetAllCategories();
				this.hasSearched.set(false);
				return;
			}
			this.executeSearch(searchTerm.trim());
		});
	}

	getDisplayedItems(category: SearchCategory): SearchResultItem[] {
		const allItems = category.items();
		return category.showAll() ? allItems : allItems.slice(0, INITIAL_DISPLAY_COUNT);
	}

	hasMoreItems(category: SearchCategory): boolean {
		return !category.showAll() && category.totalCount() > INITIAL_DISPLAY_COUNT;
	}

	expandCategory(category: SearchCategory): void {
		category.showAll.set(true);
	}

	onSearchInput(event: Event): void {
		const inputValue = (event.target as HTMLInputElement).value;
		this.searchInput$.next(inputValue);
	}

	private buildIconUrl(area: number): string {
		const base64 = this.sessionService.cachedFavIconsByArea.get(area);
		return base64 ? `data:image/png;base64,${base64}` : '';
	}

	private getServiceCompanyName(area: number): string {
		return this.sessionService.cachedServiceCompanyNames?.get(area)?.replace(',', '') ?? '';
	}

	private formatAddress(street: string, zipCode: string, city: string): string {
		return [street, `${zipCode} ${city}`].filter(Boolean).join(', ');
	}

	private resetAllCategories(): void {
		for (const category of this.categories) {
			category.items.set([]);
			category.totalCount.set(0);
			category.showAll.set(false);
			category.loading.set(false);
		}
		this.cachedRealEstateContacts = [];
	}

	private executeSearch(searchValue: string): void {
		this.hasSearched.set(true);
		this.resetAllCategories();
		this.realEstateCategory.loading.set(true);
		this.billingCategory.loading.set(true);

		this.loadRealEstatesWithPagination(searchValue);
	}

	private loadRealEstatesWithPagination(searchValue: string): void {
		const query: ViewQuery = { searchValue, size: MAX_PAGE_SIZE, page: 0 };

		this.realEstateService
			.search(this.sessionService.effectiveArea, query)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (firstPage: Page) => {
					const contacts = [...(firstPage.items as RealEstateContact[])];

					if (firstPage.totalElements <= contacts.length) {
						this.handleRealEstatesLoaded(contacts);
						return;
					}

					const additionalPageCount = Math.ceil((firstPage.totalElements - contacts.length) / MAX_PAGE_SIZE);
					const additionalRequests: Observable<Page>[] = [];
					for (let pageIndex = 1; pageIndex <= additionalPageCount; pageIndex++) {
						additionalRequests.push(this.realEstateService.search(this.sessionService.effectiveArea, { ...query, page: pageIndex }));
					}

					forkJoin(additionalRequests)
						.pipe(takeUntilDestroyed(this.destroyRef))
						.subscribe((additionalPages: Page[]) => {
							for (const page of additionalPages) {
								contacts.push(...(page.items as RealEstateContact[]));
							}
							this.handleRealEstatesLoaded(contacts);
						});
				},
				error: () => {
					this.realEstateCategory.loading.set(false);
					this.billingCategory.loading.set(false);
				},
			});
	}

	private handleRealEstatesLoaded(contacts: RealEstateContact[]): void {
		this.cachedRealEstateContacts = contacts;
		this.realEstateCategory.totalCount.set(contacts.length);
		this.realEstateCategory.items.set(contacts.map((contact) => this.toRealEstateResultItem(contact)));
		this.realEstateCategory.loading.set(false);

		this.loadBillingsForContacts(contacts);
	}

	private loadBillingsForContacts(contacts: RealEstateContact[]): void {
		if (contacts.length === 0) {
			this.billingCategory.loading.set(false);
			return;
		}

		const realEstatesPayload: RealEstates = {
			realEstates: contacts.map((contact) => ({
				realEstateNumber: contact.realEstateNumber,
				area: contact.area,
				realEstateId: contact.realEstateId,
			})),
		};

		this.billingCustomCService
			.getBillingsByRealEstates(realEstatesPayload)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (billings: Billing[]) => {
					const portalActiveBillings = billings.filter((billing) => billing.portalActive !== false);
					this.billingCategory.totalCount.set(portalActiveBillings.length);
					this.billingCategory.items.set(portalActiveBillings.map((billing) => this.toBillingResultItem(billing)));
					this.billingCategory.loading.set(false);
				},
				error: () => {
					this.billingCategory.loading.set(false);
				},
			});
	}

	/**
	 * Extracts the property management display name from a RealEstateContact.
	 * Uses deprecated fields (contactName/contactOverwritingName1) as no replacement exists in the API yet.
	 */
	private extractPropertyManagementName(contact: RealEstateContact): string {
		// NOSONAR: deprecated fields — no API replacement available yet
		return contact.contactName ?? contact.contactOverwritingName1 ?? '';
	}

	private toRealEstateResultItem(contact: RealEstateContact): SearchResultItem {
		const propertyManagementName = this.extractPropertyManagementName(contact);
		return {
			id: contact.realEstateId,
			iconUrl: this.sessionService.cachedFavIconsByArea.has(contact.area) ? this.buildIconUrl(contact.area) : undefined,
			iconTooltip: this.getServiceCompanyName(contact.area),
			primary: `${contact.area} / ${contact.realEstateNumber}`,
			secondary: this.formatAddress(contact.realEstateAddressStreet, contact.realEstateAddressZipCode, contact.realEstateAddressCity),
			tertiary: propertyManagementName,
			onClick: () => this.navigateToRealEstate(contact),
		};
	}

	private toBillingResultItem(billing: Billing): SearchResultItem {
		const matchingContact = this.cachedRealEstateContacts.find((contact) => contact.realEstateId === billing.realEstateId);
		const area = matchingContact?.area ?? 0;
		const realEstateNumber = matchingContact?.realEstateNumber ?? 0;
		return {
			id: billing.exportId,
			iconUrl: this.sessionService.cachedFavIconsByArea.has(area) ? this.buildIconUrl(area) : undefined,
			iconTooltip: this.getServiceCompanyName(area),
			primary: `${area} / ${realEstateNumber}`,
			meta: `-${billing.billingNumber}`,
			secondary: matchingContact
				? this.formatAddress(matchingContact.realEstateAddressStreet, matchingContact.realEstateAddressZipCode, matchingContact.realEstateAddressCity)
				: undefined,
			tertiary: billing.billingName,
			onClick: () => this.navigateToBilling(billing),
		};
	}

	private navigateToRealEstate(contact: RealEstateContact): void {
		this.dialogRef.close();
		this.router.navigate(['/realestates', contact.realEstateId]);
	}

	private navigateToBilling(billing: Billing): void {
		this.dialogRef.close();
		this.router.navigate(['/billing-units', billing.exportId]);
	}
}
